// src/utils/participantIdentity.js
//
// Pass 85 — CANONICAL PARTICIPANT IDENTITY (pure domain; no Firestore, no React).
//
// A PARTICIPANT is the human who RECEIVES coaching. It is a DISTINCT entity from:
//   • the ORGANIZATION (tenant),
//   • the CLIENT (commercial customer/account — `clientId`, may have NO Auth),
//   • the BOOKING REQUESTER (`requesterUserId`, the purchaser),
//   • the FIREBASE AUTH ACCOUNT (an OPTIONAL current link).
// These are NEVER merged. A commercial client may relate to MANY participants (a family: the payer books,
// child A skis, child B snowboards); a participant may appear across many bookings/lessons/seasons.
//
// `participantId` is a DURABLE BUSINESS identity (opaque, org-scoped), mirroring the proven `stableClientId`
// pattern. It MUST NOT equal or derive from an Auth uid, email, display name, DOB, or clientId. It is minted from
// the organization + an opaque, non-PII creation seed (a random token the writer generates), so relinking or
// replacing an Auth account NEVER changes it. Identity is created/linked ONLY by an explicit operator action —
// never inferred from a name / email / DOB / spreadsheet row / lead-guest / additional-guest string.

const str = (v) => String(v == null ? '' : v).trim();
const MAX_GOALS = 2000;

// FNV-1a (two salted rounds → 16 hex). Self-contained; an opaque STABLE id, not a security primitive. A Pass-85
// salt keeps the participant id namespace distinct from clientId (`pass63`).
function fnv1a(s) { let h = 0x811c9dc5 >>> 0; for (let i = 0; i < s.length; i += 1) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; } return h >>> 0; }
function stableHashHex(s) { const a = fnv1a(s); const b = fnv1a(`${s}pass85`); return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0'); }

export const PARTICIPANT_STATUS = Object.freeze({ ACTIVE: 'active', ARCHIVED: 'archived' });

// The ONLY fields a participant profile may carry — safe, student-facing coaching fields. Commercial / staff-CRM /
// private data is FORBIDDEN here (that lives in clientProfiles, which is NOT the student profile).
export const PARTICIPANT_SAFE_FIELDS = Object.freeze(['displayName', 'preferredName', 'primaryDiscipline', 'goals']);
export const PARTICIPANT_FORBIDDEN_FIELDS = Object.freeze([
  'clientId', 'price', 'amount', 'revenue', 'totalRevenue', 'phone', 'email', 'notesInternal', 'payment',
  'paymentState', 'visa', 'passport', 'dob', 'packageBalance', 'retentionScore', 'retentionLastReviewedAt',
  'retentionSnoozedUntil', 'tags', 'requesterUserId', 'sourceCustomerId',
]);

// ── participantId — opaque, org-scoped, from a NON-PII creation seed (never email/name/DOB/clientId/Auth uid) ──
export function stableParticipantId(organizationId, creationSeed) {
  const org = str(organizationId); const seed = str(creationSeed);
  if (!org || !seed) return '';
  return `participant_${stableHashHex(`${org}::${seed}`)}`;
}

// A guard: reject any attempt to put commercial/CRM/private data on a participant profile.
export function assertSafeParticipantFields(patch = {}) {
  for (const k of Object.keys(patch)) {
    if (PARTICIPANT_FORBIDDEN_FIELDS.includes(k)) {
      const e = new Error(`Field "${k}" is not allowed on a participant profile (commercial/CRM/private data is forbidden here).`);
      e.code = 'PARTICIPANT_FORBIDDEN_FIELD';
      throw e;
    }
  }
  return true;
}

// The canonical write shape for a participant profile. `authUserId` is the OPTIONAL current Auth link (blank until a
// participant deliberately claims an account); it is audit/access metadata, never the durable identity.
// `accountLinkGeneration` (Pass 85F.3) is the current ACCOUNT-AUTHORIZATION EPOCH: an operator UNLINK increments it, so
// every invitation issued under the old epoch (incl. pending siblings for other emails) becomes stale. A claim is valid
// ONLY when the invite's generation equals the profile's current generation. It is security metadata, never coaching data.
export function buildParticipantProfile(plan = {}) {
  return {
    participantId: str(plan.participantId),
    organizationId: str(plan.organizationId),
    displayName: str(plan.displayName),
    preferredName: str(plan.preferredName),
    primaryDiscipline: str(plan.primaryDiscipline),
    goals: str(plan.goals).slice(0, MAX_GOALS),
    authUserId: str(plan.authUserId),           // OPTIONAL current Auth link; '' when unlinked
    accountLinkGeneration: Number.isInteger(+plan.accountLinkGeneration) && +plan.accountLinkGeneration >= 0 ? Math.floor(+plan.accountLinkGeneration) : 0,
    status: plan.status === PARTICIPANT_STATUS.ARCHIVED ? PARTICIPANT_STATUS.ARCHIVED : PARTICIPANT_STATUS.ACTIVE,
    createdByAuthUserId: str(plan.createdByAuthUserId),
  };
}

// ── CLIENT ↔ PARTICIPANT relationship (a commercial client is explicitly associated with a participant) ─────────
// Deterministic collision-safe id = org::clientId::participantId (NEVER name/email). A link means ONLY "this
// participant is explicitly associated with this commercial client in this org" — it grants NO payment / CRM /
// guardian / other-participant access.
export function clientParticipantLinkId(organizationId, clientId, participantId) {
  const org = str(organizationId); const c = str(clientId); const p = str(participantId);
  if (!org || !c || !p) return '';
  return `${org}::${c}::${p}`;
}
export function buildClientParticipantLink(plan = {}) {
  return {
    organizationId: str(plan.organizationId),
    clientId: str(plan.clientId),
    participantId: str(plan.participantId),
    provenance: str(plan.provenance) || 'operator_explicit',
    createdByAuthUserId: str(plan.createdByAuthUserId),
  };
}

// ── LESSON ↔ PARTICIPANT relationship (a participant attended a delivered/scheduled session) ────────────────────
// One lessonInstance is ONE real session; a group session has MANY participant links; a participant has MANY
// lessons. The lesson is NEVER duplicated per participant. A participant coaching claim is NEVER inferred from a
// clientId match — the participant must be EXPLICITLY linked. id = org::lessonId::participantId.
export function lessonParticipantLinkId(organizationId, lessonId, participantId) {
  const org = str(organizationId); const l = str(lessonId); const p = str(participantId);
  if (!org || !l || !p) return '';
  return `${org}::${l}::${p}`;
}
export function buildLessonParticipantLink(plan = {}) {
  return {
    organizationId: str(plan.organizationId),
    lessonId: str(plan.lessonId),
    participantId: str(plan.participantId),
    clientId: str(plan.clientId),                // OPTIONAL commercial context, when explicitly known
    provenance: str(plan.provenance) || 'operator_explicit',
    createdByAuthUserId: str(plan.createdByAuthUserId),
  };
}

// ── PARTICIPANT ACCOUNT INVITE (targeted, email-verified — mirrors the org-invite pattern) ──────────────────────
// An operator prepares a narrow invitation targeting an exact (organizationId, participantId, approved email). The
// recipient signs in with a Firebase-Auth-VERIFIED email that matches; the participant profile's `authUserId` is
// then linked to the recipient's CURRENT uid (participantId unchanged). Email is a targeted claim verifier, NEVER a
// fuzzy identity-merge signal. No email provider is activated — "prepare invitation / copy link" is enough.
export const PARTICIPANT_INVITE_STATUS = Object.freeze({ PENDING: 'pending', ACCEPTED: 'accepted', DECLINED: 'declined', REVOKED: 'revoked' });
export const normalizeParticipantEmail = (v) => str(v).toLowerCase();

export function buildParticipantInvite(plan = {}) {
  return {
    organizationId: str(plan.organizationId),
    participantId: str(plan.participantId),
    emailNormalized: normalizeParticipantEmail(plan.email),
    status: PARTICIPANT_INVITE_STATUS.PENDING,
    token: str(plan.token),                       // opaque routing/copy-link value (NOT the auth secret — see below)
    // Pass 85F.3 — the participant's account-authorization EPOCH at the moment the operator issued this invite. A claim is
    // valid ONLY when this equals the profile's CURRENT generation, so an invite issued before an unlink is stale.
    accountLinkGeneration: Number.isInteger(+plan.accountLinkGeneration) && +plan.accountLinkGeneration >= 0 ? Math.floor(+plan.accountLinkGeneration) : 0,
    createdByAuthUserId: str(plan.createdByAuthUserId),
  };
}

